import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import clsx from 'clsx';
import { ResultGrid, type ResultColumnView } from './ResultGrid.js';

/**
 * One statement's output.
 *
 * A job has at least one and, for a read-only script, one per statement
 * that ran -- which on a script that failed part-way is fewer than were
 * submitted.
 */
export interface ResultSet {
  readonly seq: number;
  /** The statement that produced it. Null for an API response. */
  readonly statement: string | null;
  readonly columns: readonly ResultColumnView[];
  readonly rows: readonly (readonly unknown[])[];
  readonly truncated: boolean;
  readonly rowCount: number;
  readonly durationMs: number;
}

export interface InlineResultPage {
  readonly jobId: string;
  readonly results: readonly ResultSet[];
}

interface ResultPanelProps {
  readonly results: readonly ResultSet[];
  /** Shown at the right of the summary line. */
  readonly footnote?: ReactNode;
  /** Passed through to the grid: the parent has already decided its height. */
  readonly fills?: boolean;
}

/**
 * The result of a job, with one tab per statement when there is more than
 * one.
 *
 * Tabs rather than a stack of grids. Several grids down a scrolling page
 * means the second one is a few hundred pixels tall and the fifth is not on
 * screen at all, which is worse than useless for the case this is for --
 * running a handful of reads and comparing what they say.
 */
export function ResultPanel({
  results,
  footnote,
  fills = false,
}: ResultPanelProps): ReactElement | null {
  const [selected, setSelected] = useState(0);

  /**
   * A new job's results arrive under the same component, and the tab the
   * user was on may not exist any more -- a script of six followed by a
   * single statement would otherwise render nothing at all.
   */
  useEffect(() => {
    setSelected((current) => (current < results.length ? current : 0));
  }, [results]);

  if (results.length === 0) return null;

  const active = results[selected] ?? results[0]!;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {results.length > 1 ? (
        <div
          role="tablist"
          aria-label="Statement results"
          className="flex flex-wrap items-center gap-1 border-b border-line px-2 py-1"
        >
          {results.map((set, index) => (
            <button
              key={set.seq}
              type="button"
              role="tab"
              aria-selected={index === selected}
              onClick={() => setSelected(index)}
              /*
                The statement and its size, so a tab can be identified
                without selecting it. Numbers alone make the user click
                through all six to find the one they meant -- and putting
                the row count on the tab face instead reads as "1 3", two
                numbers meaning different things side by side.
              */
              title={`${set.rowCount.toLocaleString()} row${set.rowCount === 1 ? '' : 's'}${
                set.statement === null ? '' : ` · ${set.statement}`
              }`}
              className={clsx(
                'rounded px-2 py-1 text-xs',
                index === selected
                  ? 'bg-accent text-accent-fg'
                  : 'text-muted hover:bg-surface-2 hover:text-fg',
              )}
            >
              {index + 1}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-3 border-b border-line px-3 py-2 text-xs text-muted">
        <span>
          {active.rowCount.toLocaleString()} row{active.rowCount === 1 ? '' : 's'}
        </span>
        <span>{active.durationMs.toLocaleString()} ms</span>
        {active.statement !== null && results.length > 1 ? (
          <span className="truncate font-mono text-subtle" title={active.statement}>
            {active.statement}
          </span>
        ) : null}
        {footnote ? <span className="ml-auto shrink-0">{footnote}</span> : null}
      </div>

      {/*
        Keyed on the statement, so switching tabs builds a new grid rather
        than handing different columns to the existing one. AG Grid can be
        re-columned in place, but the wrap toggle and column widths are the
        grid's own state and carrying them across to an unrelated result is
        not what anyone means by switching tabs.
      */}
      <ResultGrid
        key={active.seq}
        columns={active.columns}
        rows={active.rows}
        truncated={active.truncated}
        fills={fills}
      />
    </div>
  );
}
